import { newId } from '../doc/BoardDoc';
import { rotatedBounds, unionRects, type Rect } from '../doc/geometry';
import type { Item, ItemPatch } from '../doc/schema';
import { selectedItems, type Ctx } from './actions';

/** Alignment, distribution, size normalising, grid layout, grouping and locking of the selection. */

export type AlignMode = 'left' | 'centerX' | 'right' | 'top' | 'centerY' | 'bottom';

function movable(ctx: Ctx): Item[] {
  return selectedItems(ctx).filter((i) => !ctx.board.isLocked(i));
}

function withBounds(items: Item[]): { item: Item; b: Rect }[] {
  return items.map((item) => ({ item, b: rotatedBounds(item) }));
}

export function alignSelection(ctx: Ctx, mode: AlignMode): void {
  const items = withBounds(movable(ctx));
  if (items.length < 2) return;
  const u = unionRects(items.map((i) => i.b));
  if (!u) return;
  const patches: [string, ItemPatch][] = [];
  for (const { item, b } of items) {
    let dx = 0;
    let dy = 0;
    switch (mode) {
      case 'left':
        dx = u.x - b.x;
        break;
      case 'centerX':
        dx = u.x + u.w / 2 - (b.x + b.w / 2);
        break;
      case 'right':
        dx = u.x + u.w - (b.x + b.w);
        break;
      case 'top':
        dy = u.y - b.y;
        break;
      case 'centerY':
        dy = u.y + u.h / 2 - (b.y + b.h / 2);
        break;
      case 'bottom':
        dy = u.y + u.h - (b.y + b.h);
        break;
    }
    if (dx || dy) patches.push([item.id, { x: round(item.x + dx), y: round(item.y + dy) }]);
  }
  if (patches.length) ctx.board.updateItems(patches);
}

/** Equal gaps between items along one axis; the outermost items stay put. */
export function distributeSelection(ctx: Ctx, axis: 'x' | 'y'): void {
  const items = withBounds(movable(ctx));
  if (items.length < 3) return;
  const key = axis === 'x' ? 'x' : 'y';
  const size = axis === 'x' ? 'w' : 'h';
  items.sort((a, b) => a.b[key] - b.b[key]);
  const first = items[0]!;
  const last = items[items.length - 1]!;
  const span = last.b[key] + last.b[size] - first.b[key];
  const total = items.reduce((s, i) => s + i.b[size], 0);
  const gap = (span - total) / (items.length - 1);
  const patches: [string, ItemPatch][] = [];
  let cursor = first.b[key] + first.b[size] + gap;
  for (let i = 1; i < items.length - 1; i++) {
    const { item, b } = items[i]!;
    const delta = cursor - b[key];
    if (Math.abs(delta) > 0.01) patches.push([item.id, { [key]: round(item[key] + delta) }]);
    cursor += b[size] + gap;
  }
  if (patches.length) ctx.board.updateItems(patches);
}

/**
 * Give every selected item the same width, height, or longest side as the first selected one,
 * keeping each item's aspect ratio.
 */
export function normalizeSelection(ctx: Ctx, mode: 'width' | 'height' | 'longest'): void {
  const items = movable(ctx);
  if (items.length < 2) return;
  const ref = items[0]!;
  const target = mode === 'width' ? ref.w : mode === 'height' ? ref.h : Math.max(ref.w, ref.h);
  const patches: [string, ItemPatch][] = [];
  for (const item of items.slice(1)) {
    const base = mode === 'width' ? item.w : mode === 'height' ? item.h : Math.max(item.w, item.h);
    if (base <= 0) continue;
    const s = target / base;
    if (Math.abs(s - 1) < 0.001) continue;
    // scale about the centre so the composition does not drift
    const cx = item.x + item.w / 2;
    const cy = item.y + item.h / 2;
    const w = item.w * s;
    const h = item.h * s;
    patches.push([item.id, { x: round(cx - w / 2), y: round(cy - h / 2), w: round(w), h: round(h) }]);
  }
  if (patches.length) ctx.board.updateItems(patches);
}

/** Lay the selection out in a grid (rows by current position), keeping sizes. */
export function arrangeGrid(ctx: Ctx, gap = 24, columns?: number): void {
  const items = withBounds(movable(ctx));
  if (items.length < 2) return;
  const u = unionRects(items.map((i) => i.b))!;
  // reading order: by row band (top edge, quantised), then x
  const band = Math.max(1, Math.min(...items.map((i) => i.b.h)) / 2);
  items.sort((a, b) => Math.round(a.b.y / band) - Math.round(b.b.y / band) || a.b.x - b.b.x);
  const cols = columns ?? Math.ceil(Math.sqrt(items.length));
  const cellW = Math.max(...items.map((i) => i.b.w));
  const patches: [string, ItemPatch][] = [];
  let y = u.y;
  for (let r = 0; r * cols < items.length; r++) {
    const row = items.slice(r * cols, (r + 1) * cols);
    const rowH = Math.max(...row.map((i) => i.b.h));
    row.forEach(({ item, b }, c) => {
      const targetX = u.x + c * (cellW + gap) + (cellW - b.w) / 2;
      const targetY = y + (rowH - b.h) / 2;
      const dx = targetX - b.x;
      const dy = targetY - b.y;
      if (dx || dy) patches.push([item.id, { x: round(item.x + dx), y: round(item.y + dy) }]);
    });
    y += rowH + gap;
  }
  if (patches.length) ctx.board.updateItems(patches);
}

// ---- groups -----------------------------------------------------------------

export function groupSelection(ctx: Ctx): void {
  const items = selectedItems(ctx);
  if (items.length < 2) return;
  const groupId = newId();
  ctx.board.updateItems(items.map((i): [string, ItemPatch] => [i.id, { groupId }]));
}

export function ungroupSelection(ctx: Ctx): void {
  const items = selectedItems(ctx).filter((i) => i.groupId);
  if (!items.length) return;
  ctx.board.updateItems(items.map((i): [string, ItemPatch] => [i.id, { groupId: null }]));
}

/** Ids of every member of the groups these ids belong to (plus the ids themselves). */
export function expandGroups(ctx: Ctx, ids: Iterable<string>): string[] {
  const out = new Set<string>();
  for (const id of ids) {
    out.add(id);
    const item = ctx.board.index.get(id);
    if (item?.groupId) for (const m of ctx.board.index.membersOf(item.groupId)) out.add(m);
  }
  return [...out];
}

// ---- locking -----------------------------------------------------------------

/** Locks the selection, or unlocks it when every selected item is already locked. */
export function toggleLockSelection(ctx: Ctx): void {
  const items = selectedItems(ctx);
  if (!items.length) return;
  const allLocked = items.every((i) => i.locked);
  ctx.board.updateItems(items.map((i): [string, ItemPatch] => [i.id, { locked: !allLocked }]));
}

const round = (v: number) => Math.round(v * 100) / 100;
